import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { DeploymentService } from './deployment.service';
import { AppService } from '../../application/app.service';
import { AppStatus } from '../../application/app.datamodel';
import { PermissionsService } from '../../user/permissions/permissions.service';
import { ActivationParametersService } from '../../activation-parameters/activation-parameters.service';
import { PermissionsType } from '../../user/permissions/permissions.datamodel';
import { ActorObject, ActorType } from '../../shared/datamodel';
import { MULTI_ORG_VIEW_COMPONENTS_TYPES } from '../../publish/component/store/component.datamodel';
import { DeploymentLevel } from './deployment.datamodel';
import { DetailedAppWithConfigurations } from '../../configuration/componentsWithConfigs.datamodel';

import * as DeploymentActions from './deployment.actions';
import * as AppActions from '../../application/app.actions';
import * as PermissionsActions from '../../user/permissions/permissions.actions';
import * as ConfigurationActions from '../../configuration/store/configuration.actions';
import * as CoreActions from '../../core/store/core.actions';

@Injectable()
export class DeploymentEffects {
  /**
   * Triggers the initial state
   */
  initializeDeployments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.AppCollapseDetails,
        AppActions.AppExpandDetails
      ),
      mergeMap(() => {
        return of(DeploymentActions.InitializeDeploymentState());
      })
    )
  );

  /**
   * After initializing the deployment state, the Wallet App is selected by default
   */
  entitySelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DeploymentActions.InitializeDeploymentState
      ),
      withLatestFrom(this.appService.entitySelected$),
      mergeMap(([_, entity]) => {
        return of(DeploymentActions.LssEntitySelected({
          entity: {
            actorName: entity.label ?? '',
            actorType: ActorType.ORGANISATION
          }
        }));
      })
    )
  );

  /**
   * Load the user permission and ACLs to manage deployments
   */
  deploymentPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PermissionsActions.RetrievedTaAdminOffices,
      ),
      mergeMap(() => {
        const viewAccessORGs = this.permissionsService.getPermissionsACLs(PermissionsType.VIEW_INVENTORY);
        const allowedORGs = this.permissionsService.getPermissionsACLs(PermissionsType.MANAGE_MARKET_CATALOG);
        const allowedOffices = this.permissionsService.getPermissionsACLs(PermissionsType.MANAGE_SCRIPT_CONFIGURATION);
        return of(DeploymentActions.DeploymentPermissionLoaded(
          {
            viewAccessORGs,
            allowedORGs,
            allowedOffices
          }));
      })
    )
  );

  /**
   * Load if the user can display multiple ORGs on the selected App
   */
  displayMultipleORGs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.RetrieveDetailsCompleted,
      ),
      withLatestFrom(this.appService.detailedApp$),
      mergeMap(([_, app]) => {
        return of(DeploymentActions.DisplayMultipleORGsLoaded(
          {
            isEnabled: this.permissionsService.isUserAuthorized(PermissionsType.VIEW_INVENTORY) &&
              this.permissionsService.isUserAuthorized(PermissionsType.DISPLAY_ALL_ORG) &&
              this.isAppCompatibleWithMultiORGView(app)
          })
        );
      })
    )
  );

  /**
   * Load the activation parameter for OGUs deployment
   */
  isOGUsDeploymentEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CoreActions.EntitySelected
      ),
      withLatestFrom(this.appService.entitySelected$),
      mergeMap(([_, entity]) => {
        return of(DeploymentActions.IsOGUsDeploymentEnabled(
          {
            isEnabled: !(
                this.permissionsService.isUserAuthorized(PermissionsType.MANAGE_SCRIPT_CONFIGURATION) &&
                !this.permissionsService.isUserAuthorized(PermissionsType.VIEW_INVENTORY, entity.label) &&
                !this.permissionsService.isUserAuthorized(PermissionsType.MANAGE_MARKET_CATALOG, entity.label))
          })
        );
      })
    )
  );

  /**
   * Removes uncommitted changes for selected App
   */
  removeUncommittedChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.ChangePushStatus,
        DeploymentActions.PopulateUserDeploymentsFromSelectedOffice,
        ConfigurationActions.UpdateScriptConfigurationCompleted),
      withLatestFrom(this.appService.detailedApp$),
      mergeMap(([_input, app]) => {
        if (app.id) {
          return of(DeploymentActions.RemoveUncommittedChanges({appId: app.id}));
        } else {
          return EMPTY;
        }
      })
    )
  );

  /**
   * Removes uncommitted changes for selected App, to keep the old deployment UI working (used in user deployment flows)
   */
  removeUncommittedChangesForUserDeployment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.InstallUninstallCompleted,
        AppActions.MassiveInstallUninstallCompleted),
      withLatestFrom(this.appService.detailedApp$, this.deploymentService.selectedDeploymentLevelCategory$),
      mergeMap(([_input, app, deploymentLevel]) => {
        if (deploymentLevel?.level === DeploymentLevel.USER && app.id) {
          return of(DeploymentActions.RemoveUncommittedChanges({appId: app.id}));
        } else {
          return EMPTY;
        }
      })
    )
  );

  /**
   * Updates configurations for the deployed entities of the selected App
   */
  updateDeploymentsConfigurations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ConfigurationActions.UpdateScriptConfigurationCompleted,
        ConfigurationActions.UpdateScriptConfigurationPartiallyCompleted,
        ConfigurationActions.MassiveUpdateScriptConfigurationCompleted
      ),
      withLatestFrom(this.appService.detailedApp$),
      mergeMap(([_input, app]) => {
        return of(DeploymentActions.UpdateDeployedEntitiesConfigurations({
          appId: app.id!,
          deployedActors: this.deploymentService.removeOrgFromDeployedUserOrg(
            app.deployedActors
          )
        }));
      })
    )
  );

  /**
   * Populates users into selected App
   */
  populateUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.RetrieveDetailsCompleted,
        AppActions.InstallUninstallCompleted,
        AppActions.MassiveInstallUninstallCompleted
      ),
      withLatestFrom(this.appService.detailedApp$, this.appService.entitySelected$),
      mergeMap(([_input, app, entity]) => {
        return of(DeploymentActions.PopulateUserDeployments(
          {
            userDeployments: this.deploymentService.buildUserDeployments(
              app.deployedActors,
              app.cmpsWithConfigs?.components,
              entity.label ?? ""),
            appId: app.id!
          }
        ));
      })
    )
  );

  /**
   * Populates offices into selected App
   */
  populateOffices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.RetrieveDetailsCompleted,
        AppActions.InstallUninstallCompleted,
        AppActions.MassiveInstallUninstallCompleted
      ),
      withLatestFrom(this.appService.detailedApp$, this.appService.officeIds$),
      mergeMap(([_input, appDetails, officeIds]) => {
        return of(DeploymentActions.PopulateOfficeDeployments(
          {
            officeDeployments: this.deploymentService.buildOfficeDeployments(
              officeIds,
              appDetails.deployedActors,
              appDetails.cmpsWithConfigs.components),
            appId: appDetails.id!
          }
        ));
      })
    )
  );

  /**
   * Populates initial deployed entities
   */
  populateInitialLssDeployments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DeploymentActions.DisplayMultipleORGsLoaded,
        AppActions.RetrieveAppDeploymentsByORGCompleted
      ),
      withLatestFrom(this.appService.detailedApp$, this.appService.entitySelected$),
      mergeMap(([action, detailedApp, org] ) => {
        if (detailedApp.deployedActors) {
          const targetOrg: ActorObject = 'targetOrg' in action ? action.targetOrg :
            {actorType: ActorType.ORGANISATION, actorName: org.label ?? ''};
          return of(DeploymentActions.PopulateDeployedLssEntities(
            {
              entities: detailedApp.deployedActors
                .map((deployedActor: {actor: ActorObject}) => deployedActor.actor),
              targetOrg
            }
          ));
        }
        return EMPTY;
      })
    )
  );

  /**
   * Filter the ORGs that the user can manage with the selected App scope
   */
  filterLssORGsWithAppScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DeploymentActions.DisplayMultipleORGsLoaded
      ),
      withLatestFrom(
        this.appService.selectedAppScope$,
        this.appService.entitySelected$,
        this.deploymentService.isMultiORGViewEnabled$
      ),
      mergeMap(([_,
                  selectedAppScope,
                  selectedOrg,
                  isMultiORGViewEnabled
                ]) => {
        const entities: ActorObject[] = [];
        if (isMultiORGViewEnabled) {
          const acls = this.permissionsService.getPermissionsACLs(PermissionsType.VIEW_INVENTORY);
          const aclEntities = selectedAppScope.some(entity => entity.actorName === '[ALL]') ?
            acls :
            selectedAppScope.filter((entity) => acls.some(acl => acl.actorName === entity.actorName)) ?? [];
          entities.push(...aclEntities);
        } else {
          entities.push({
            actorName: selectedOrg.label ?? '',
            actorType: ActorType.ORGANISATION
          });
        }
        return of(DeploymentActions.FilterORGsWithAppScope(
          {
            entities
          }
        ));
      })
    )
  );

  /**
   * Populate deployed LSS entities
   */
  populateLssDeployments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.InstallUninstallCompleted,
        AppActions.MassiveInstallUninstallPartial,
        AppActions.MassiveInstallUninstallCompleted
      ),
      mergeMap((action) => {
        return of(DeploymentActions.PopulateDeployedLssEntities(
          {
            entities: action.installActionStatus
            .filter((deployedActor: {status: AppStatus, target: ActorObject}) =>
              deployedActor.status === AppStatus.INSTALLED)
            .map((deployedActor: {status: AppStatus, target: ActorObject}) => deployedActor.target)
          }
        ));
      })
    )
  );

  /**
   * Populate deployed LSS entities
   */
  populateLssUndeployments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.InstallUninstallCompleted,
        AppActions.MassiveInstallUninstallPartial,
        AppActions.MassiveInstallUninstallCompleted
      ),
      mergeMap((action) => {
        return of(DeploymentActions.PopulateUndeployedLssEntities(
          {
            entities: action.installActionStatus
              .filter((deployedActor: {status: AppStatus, target: ActorObject}) =>
                deployedActor.status === AppStatus.UNINSTALLED)
              .map((deployedActor: {status: AppStatus, target: ActorObject}) => deployedActor.target)
          }
        ));
      })
    )
  );

  /**
   * Populates all excluded LSS entities
   */
  populateInitialLssExclusions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.RetrieveDetailsCompleted,
        AppActions.RetrieveAppDeploymentsByORGCompleted
      ),
      mergeMap((input) => {
        if (input.excludedActors) {
          return of(DeploymentActions.PopulateExcludedLssEntities(
            {
              entities: input.excludedActors
            }
          ));
        }
        return EMPTY;
      })
    )
  );

  /**
   * Changes the selected office for user retrieval
   */
  changeSelectedOffice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DeploymentActions.RetrievingUsersFromSelectedOfficeFailure
      ),
      mergeMap(() => {
        return of(DeploymentActions.ChangeSelectedOffice({selectedOfficeName: undefined}));
      })
    )
  );

  /**
   * When applying unsaved changes from the 'apply changes?' modal, we should wait for the app install to be finished
   * before dispatching the action that it was closed, to ensure no other calls are done in parallel.
   */
  unsavedChangesModalClosed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.InstallUninstallCompleted,
        AppActions.MassiveInstallUninstallPartial,
        AppActions.MassiveInstallUninstallCompleted
      ),
      withLatestFrom(this.deploymentService.isUnsavedChangesModalResolved$, this.deploymentService.isUnsavedChangesModalTriggered$),
      mergeMap(([_input, isModalResolved, isModalTriggered]) => {
        if (isModalTriggered && !isModalResolved) {
          return of(DeploymentActions.UnsavedChangesModalClosed());
        }
        return EMPTY;
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly deploymentService: DeploymentService,
    private readonly permissionsService: PermissionsService,
    private readonly activationParametersService: ActivationParametersService,
    private readonly appService: AppService) {
  }

  /**
   * Check if the app is compatible with the multi ORG view.
   * The multi ORG view is only available for apps that have no components or have the SECO_FARM_MAP component.
   * @param app
   * @private
   */
  private isAppCompatibleWithMultiORGView(app: DetailedAppWithConfigurations) {
    return MULTI_ORG_VIEW_COMPONENTS_TYPES.includes(
      this.appService.getAppComponentsType(app?.components, app?.applicationType)
    );
  }
}
